import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    Grid,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle,
    Warning,
    Error
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosWithAuth from '../utils/axiosWithAuth';
import { isCASOnlySubscription } from '../utils/subscriptionUtils';

const SubscriptionsPage = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [subscriptions, setSubscriptions] = useState([]);
    const [plans, setPlans] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [statusFilter, setStatusFilter] = useState('active'); // Filter for active/deleted subscriptions
    const [subscriptionForm, setSubscriptionForm] = useState({
        plan_id: '',
        subscription_name: '',
        status: 'active'
    });

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        // Handle new parameter from URL to open create dialog
        const isNew = searchParams.get('new');
        if (isNew === '1') {
            setSelectedSubscription(null);
            setSubscriptionForm({ plan_id: '', status: 'active' });
            setOpenDialog(true);
            setSearchParams({});
            return;
        }
        // Handle edit parameter from URL
        const editId = searchParams.get('edit');
        if (editId) {
            const subscription = subscriptions.find(sub => sub.id === parseInt(editId));
            if (subscription) {
                setSelectedSubscription(subscription);
                setSubscriptionForm({
                    plan_id: subscription.plan_id.toString(),
                    status: subscription.status
                });
                setOpenDialog(true);
                // Clear the edit parameter from URL
                setSearchParams({});
            }
        }
    }, [searchParams, subscriptions]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [subscriptionsRes, plansRes] = await Promise.all([
                axiosWithAuth.get('/subscriptions/my-subscriptions'),
                axiosWithAuth.get('/subscriptions/plans')
            ]);

            setSubscriptions(subscriptionsRes.data || []);
            setPlans(plansRes.data || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to fetch data: ' + (error.response?.data?.detail || error.message));
            // Set default values to prevent map errors
            setSubscriptions([]);
            setPlans([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateSubscription = async () => {
        try {
            setLoading(true);
            await axiosWithAuth.post('/subscriptions/subscribe', subscriptionForm);
            setSuccess('Subscription created successfully!');
            setOpenDialog(false);
            setSubscriptionForm({ plan_id: '', subscription_name: '', status: 'active' });
            fetchData();
        } catch (error) {
            setError('Failed to create subscription: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSubscription = async () => {
        try {
            setLoading(true);
            await axiosWithAuth.put(`/subscriptions/${selectedSubscription.id}`, subscriptionForm);
            setSuccess('Subscription updated successfully!');
            setOpenDialog(false);
            setSelectedSubscription(null);
            setSubscriptionForm({ plan_id: '', subscription_name: '', status: 'active' });
            fetchData();
        } catch (error) {
            setError('Failed to update subscription: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteSubscription = async (subscriptionId) => {
        if (window.confirm('Are you sure you want to cancel this subscription?')) {
            try {
                setLoading(true);
                await axiosWithAuth.put(`/subscriptions/${subscriptionId}/cancel`);
                setSuccess('Subscription cancelled successfully!');
                fetchData();
            } catch (error) {
                setError('Failed to cancel subscription: ' + (error.response?.data?.detail || error.message));
            } finally {
                setLoading(false);
            }
        }
    };

    const openEditSubscription = (subscription) => {
        setSelectedSubscription(subscription);
        setSubscriptionForm({
            plan_id: subscription.plan_id,
            subscription_name: subscription.subscription_name || '',
            status: subscription.status
        });
        setOpenDialog(true);
    };

    const openCreateSubscription = () => {
        setSelectedSubscription(null);
        setSubscriptionForm({
            plan_id: '',
            subscription_name: '',
            status: 'active'
        });
        setOpenDialog(true);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'active':
                return <CheckCircle color="success" />;
            case 'cancelled':
                return <Error color="error" />;
            case 'suspended':
                return <Warning color="warning" />;
            default:
                return <Warning color="warning" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'cancelled':
                return 'error';
            case 'suspended':
                return 'warning';
            default:
                return 'default';
        }
    };

    // Mobile-friendly subscription card component
    const SubscriptionCard = ({ subscription }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {subscription.subscription_name || subscription.plan?.name || 'Unnamed Subscription'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            {subscription.subscription_name ?
                                `Plan: ${subscription.plan?.name || 'Unknown Plan'}` :
                                (subscription.plan?.description || 'No description')
                            }
                        </Typography>
                        {isCASOnlySubscription(subscription) && (
                            <Chip
                                label="CAS Only"
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                            />
                        )}
                    </Box>
                    <Chip
                        icon={getStatusIcon(subscription.status)}
                        label={subscription.status}
                        color={getStatusColor(subscription.status)}
                        size="small"
                        sx={{ flexShrink: 0 }}
                    />
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Price</Typography>
                        {isCASOnlySubscription(subscription) ? (
                            <Typography variant="body2" color="textSecondary">
                                CAS Access Only
                            </Typography>
                        ) : (
                            <Typography variant="body2" fontWeight="bold">
                                ${subscription.plan?.price_monthly || 0}/month
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Start Date</Typography>
                        <Typography variant="body2">
                            {subscription.current_period_start ?
                                new Date(subscription.current_period_start).toLocaleDateString() :
                                'N/A'
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">End Date</Typography>
                        <Typography variant="body2">
                            {subscription.current_period_end ?
                                new Date(subscription.current_period_end).toLocaleDateString() :
                                'N/A'
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Features</Typography>
                        <Typography variant="body2">
                            Content: {subscription.plan?.features?.content_generation || 0}
                        </Typography>
                        <Typography variant="body2">
                            Images: {subscription.plan?.features?.image_generation || 0}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Tooltip title="Edit Subscription">
                        <IconButton
                            size="medium"
                            onClick={() => openEditSubscription(subscription)}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Subscription">
                        <IconButton
                            size="medium"
                            color="error"
                            onClick={() => handleDeleteSubscription(subscription.id)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );

    // Filter subscriptions based on selected status
    const getFilteredSubscriptions = () => {
        if (statusFilter === 'all') {
            return subscriptions;
        }
        return subscriptions.filter(subscription => subscription.status === statusFilter);
    };

    if (loading && subscriptions.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    Subscriptions
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreateSubscription}
                    disabled={loading}
                >
                    New Subscription
                </Button>
            </Box>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label="Subscriptions" />
                </Tabs>
            </Paper>

            {/* Subscriptions Tab */}
            {activeTab === 0 && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 3,
                        gap: { xs: 2, sm: 0 }
                    }}>
                        <Typography variant="h6">
                            Your Subscriptions
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, width: { xs: '100%', sm: 'auto' } }}>
                            <InputLabel>Filter by Status</InputLabel>
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                label="Filter by Status"
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="cancelled">Cancelled</MenuItem>
                                <MenuItem value="suspended">Suspended</MenuItem>
                                <MenuItem value="all">All Subscriptions</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {isMobile ? (
                        // Mobile view - Cards
                        <Box>
                            {Array.isArray(getFilteredSubscriptions()) && getFilteredSubscriptions().map((subscription) => (
                                <SubscriptionCard key={subscription.id} subscription={subscription} />
                            ))}
                        </Box>
                    ) : (
                        // Desktop view - Table
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Plan</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Price</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Array.isArray(getFilteredSubscriptions()) && getFilteredSubscriptions().map((subscription) => (
                                        <TableRow key={subscription.id}>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {subscription.subscription_name || subscription.plan?.name || 'Unnamed Subscription'}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {subscription.subscription_name ?
                                                            `Plan: ${subscription.plan?.name || 'Unknown Plan'}` :
                                                            (subscription.plan?.description || 'No description')
                                                        }
                                                    </Typography>
                                                    {isCASOnlySubscription(subscription) && (
                                                        <Chip
                                                            label="CAS Only"
                                                            size="small"
                                                            color="secondary"
                                                            variant="outlined"
                                                            sx={{ mt: 0.5 }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={getStatusIcon(subscription.status)}
                                                    label={subscription.status}
                                                    color={getStatusColor(subscription.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {isCASOnlySubscription(subscription) ? (
                                                    <Typography variant="body2" color="textSecondary">
                                                        CAS Access Only
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="body2" fontWeight="bold">
                                                        ${subscription.plan?.price_monthly || 0}/month
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {subscription.current_period_start ?
                                                    new Date(subscription.current_period_start).toLocaleDateString() :
                                                    'N/A'
                                                }
                                            </TableCell>
                                            <TableCell>
                                                {subscription.current_period_end ?
                                                    new Date(subscription.current_period_end).toLocaleDateString() :
                                                    'N/A'
                                                }
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={1} flexWrap="wrap">
                                                    <Tooltip title="Edit Subscription">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEditSubscription(subscription)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Subscription">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteSubscription(subscription.id)}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {Array.isArray(getFilteredSubscriptions()) && getFilteredSubscriptions().length === 0 && (
                        <Box textAlign="center" py={4}>
                            <Typography variant="body1" color="textSecondary">
                                {statusFilter === 'all'
                                    ? 'No subscriptions found. Create your first subscription to get started.'
                                    : `No ${statusFilter} subscriptions found. Try selecting a different status filter.`
                                }
                            </Typography>
                        </Box>
                    )}
                </Paper>
            )}

            {/* Available Plans */}
            <Grid container spacing={3} sx={{ mt: 3 }}>
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                        Available Plans
                    </Typography>
                </Grid>
                {Array.isArray(plans) && plans.map((plan) => (
                    <Grid item xs={12} md={4} key={plan.id}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {plan.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    {plan.description}
                                </Typography>
                                <Typography variant="h5" color="primary" gutterBottom>
                                    ${plan.price_monthly}/month
                                </Typography>
                                <Box mt={2}>
                                    <Typography variant="body2" gutterBottom>
                                        Features:
                                    </Typography>
                                    <Box>
                                        <Typography variant="body2">
                                            • Content Generation: {plan.features?.content_generation || 0}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Image Generation: {plan.features?.image_generation || 0}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Blog Generation: {plan.features?.blog_generation || 0}
                                        </Typography>
                                        <Typography variant="body2">
                                            • Max Users: {plan.features?.max_users || 0}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Subscription Dialog */}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    {selectedSubscription ? 'Edit Subscription' : 'Create New Subscription'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Subscription Name"
                                value={subscriptionForm.subscription_name}
                                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_name: e.target.value })}
                                placeholder="e.g., My Business Website, Client Project, etc."
                                helperText="Give your subscription a descriptive name to easily identify it"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Plan</InputLabel>
                                <Select
                                    value={subscriptionForm.plan_id}
                                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, plan_id: e.target.value })}
                                    label="Plan"
                                >
                                    {Array.isArray(plans) && plans.map((plan) => (
                                        <MenuItem key={plan.id} value={plan.id}>
                                            {plan.name} - ${plan.price_monthly}/month
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={subscriptionForm.status}
                                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, status: e.target.value })}
                                    label="Status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="suspended">Suspended</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={selectedSubscription ? handleUpdateSubscription : handleCreateSubscription}
                        variant="contained"
                        disabled={loading}
                    >
                        {selectedSubscription ? 'Update' : 'Create'} Subscription
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SubscriptionsPage; 